import { PricingTypes } from '../../../common/constants'
import { getAdminErrorMessage } from '../adminErrorModel'
import { NotAvailable, formatCents, formatVisitDate, workareaTitle } from '../adminJobLookupModel'
import { JobOption } from '../RebuildSnapshots/rebuildSnapshotsModel'
import {
  DiscrepancyJob,
  InvoicedLine,
  InvoicedLineChoice,
  InvoicedLineKinds,
  InvoicedLineResolution,
  InvoicedLineResolutions,
  MarkUnbillableRequest,
  MatchBilledPriceRequest,
  ResolveInvoicedLinesRequest,
  UnbilledLine,
  UnbilledWorkarea,
  UnbilledWorkRequest,
  UnbilledWorkResult,
} from './markUnbillableApi'

export type UnbilledWorkFormState = {
  tenantId: number | undefined
  jobId: number | undefined
}

export type UnbilledLineRow = {
  key: string
  kind: string
  name: string
  quantity: string
  visitDate: string
  amount: string
}

export const defaultUnbilledLine = (): UnbilledLine => ({
  itemId: '',
  name: '',
  quantity: 0,
  units: '',
  visitDate: null,
  amount: 0,
})

export const defaultUnbilledWorkarea = (): UnbilledWorkarea => ({
  workareaId: 0,
  workareaName: '',
  groupName: '',
  pricingType: PricingTypes.TimeAndMaterials,
  items: [],
  labor: [],
  total: 0,
})

export type InvoicedLineChoices = Record<string, InvoicedLineResolution>

export type UnchargedLineRow = {
  key: string
  lineId: string
  workarea: string
  kind: string
  name: string
  quantity: string
  visitDate: string
  amount: string
  invoices: string
}

export type RepricedLineRow = {
  key: string
  lineId: string
  matchable: boolean
  workarea: string
  kind: string
  name: string
  visitDate: string
  invoices: string
  billedAmount: string
  amount: string
  difference: string
}

export type MatchSummary = {
  writtenOff: number
  overcharge: number
  balanceChange: number
}

export type ChoiceSummary = {
  writeOffCount: number
  writeOffTotal: number
  billCount: number
  billTotal: number
}

export const defaultInvoicedLine = (): InvoicedLine => ({
  lineId: '',
  kind: InvoicedLineKinds.Item,
  workareaId: 0,
  workareaName: '',
  groupName: '',
  name: '',
  quantity: 0,
  units: '',
  visitDate: null,
  amount: 0,
  billedAmount: 0,
  invoiceNumbers: [],
})

export const defaultDiscrepancyJob = (): DiscrepancyJob => ({
  id: 0,
  name: '',
  customerName: '',
  unchargedAmount: 0,
  unchargedLineCount: 0,
  repricedLineCount: 0,
  repricedDifference: 0,
})

export const defaultUnbilledWorkResult = (): UnbilledWorkResult => ({
  jobId: 0,
  workareas: [],
  unchargedLines: [],
  repricedLines: [],
})

export const toUnbilledWorkRequest = (state: UnbilledWorkFormState): UnbilledWorkRequest | null => {
  if (state.tenantId === undefined || state.jobId === undefined) {
    return null
  }
  return { tenantId: state.tenantId, jobId: state.jobId }
}

export const toMarkUnbillableRequest = (
  request: UnbilledWorkRequest | null,
  selectedWorkareaIds: number[]
): MarkUnbillableRequest | null => {
  if (request === null || selectedWorkareaIds.length === 0) {
    return null
  }
  return { ...request, workareaIds: selectedWorkareaIds }
}

export const selectedWorkareaIdsOn = (workareas: UnbilledWorkarea[], selectedWorkareaIds: number[]): number[] => {
  return workareas.map(workarea => workarea.workareaId).filter(id => selectedWorkareaIds.includes(id))
}

export const selectedTotal = (workareas: UnbilledWorkarea[], selectedWorkareaIds: number[]): number => {
  return workareas
    .filter(workarea => selectedWorkareaIds.includes(workarea.workareaId))
    .reduce((sum, workarea) => sum + workarea.total, 0)
}

const formatQuantity = (line: { quantity: number, units: string }): string => {
  return `${line.quantity} ${line.units}`.trim()
}

const toLineRow = (line: UnbilledLine, kind: string): UnbilledLineRow => ({
  key: `${kind}-${line.itemId}`,
  kind,
  name: line.name || NotAvailable,
  quantity: formatQuantity(line),
  visitDate: formatVisitDate(line.visitDate),
  amount: formatCents(line.amount),
})

export const toLineRows = (workarea: UnbilledWorkarea): UnbilledLineRow[] => {
  return [
    ...workarea.items.map(line => toLineRow(line, 'Item')),
    ...workarea.labor.map(line => toLineRow(line, 'Labor')),
  ]
}

const kindLabel = (line: InvoicedLine): string => {
  return line.kind === InvoicedLineKinds.Labor ? 'Labor' : 'Item'
}

const formatInvoiceNumbers = (invoiceNumbers: string[]): string => {
  return invoiceNumbers.length === 0 ? 'Not on an invoice' : invoiceNumbers.join(', ')
}

export const toUnchargedRows = (lines: InvoicedLine[]): UnchargedLineRow[] => {
  return lines.map(line => ({
    key: line.lineId,
    lineId: line.lineId,
    workarea: workareaTitle(line),
    kind: kindLabel(line),
    name: line.name || NotAvailable,
    quantity: formatQuantity(line),
    visitDate: formatVisitDate(line.visitDate),
    amount: formatCents(line.amount),
    invoices: formatInvoiceNumbers(line.invoiceNumbers),
  }))
}

const isMatchable = (line: InvoicedLine): boolean => {
  return line.kind === InvoicedLineKinds.Labor
}

export const toRepricedRows = (lines: InvoicedLine[]): RepricedLineRow[] => {
  return lines.map(line => ({
    key: line.lineId,
    lineId: line.lineId,
    matchable: isMatchable(line),
    workarea: workareaTitle(line),
    kind: kindLabel(line),
    name: line.name || NotAvailable,
    visitDate: formatVisitDate(line.visitDate),
    invoices: formatInvoiceNumbers(line.invoiceNumbers),
    billedAmount: formatCents(line.billedAmount),
    amount: formatCents(line.amount),
    difference: formatCents(line.amount - line.billedAmount),
  }))
}

export const matchableLineIdsOn = (lines: InvoicedLine[], selectedLineIds: string[]): string[] => {
  return lines
    .filter(line => isMatchable(line) && selectedLineIds.includes(line.lineId))
    .map(line => line.lineId)
}

export const hasMatchableLines = (lines: InvoicedLine[]): boolean => {
  return lines.some(isMatchable)
}

export const toMatchBilledPriceRequest = (
  request: UnbilledWorkRequest | null,
  lineIds: string[]
): MatchBilledPriceRequest | null => {
  if (request === null || lineIds.length === 0) {
    return null
  }
  return { ...request, lineIds }
}

export const summarizeMatch = (lines: InvoicedLine[], lineIds: string[]): MatchSummary => {
  return lines
    .filter(line => lineIds.includes(line.lineId))
    .reduce((summary, line) => {
      const difference = line.amount - line.billedAmount
      return {
        writtenOff: summary.writtenOff + Math.max(difference, 0),
        overcharge: summary.overcharge + Math.max(-difference, 0),
        balanceChange: summary.balanceChange - difference,
      }
    }, { writtenOff: 0, overcharge: 0, balanceChange: 0 })
}

const formatSignedCents = (cents: number): string => {
  return `${cents < 0 ? '−' : '+'}${formatCents(Math.abs(cents))}`
}

export type MatchLineDescription = {
  lineId: string
  text: string
}

export const matchLineDescriptions = (lines: InvoicedLine[], lineIds: string[]): MatchLineDescription[] => {
  return lines
    .filter(line => lineIds.includes(line.lineId))
    .map(line => ({
      lineId: line.lineId,
      text: `${workareaTitle(line)}: ${line.name || NotAvailable} (${formatVisitDate(line.visitDate)}): current ${formatCents(line.amount)}, billed ${formatCents(line.billedAmount)}`,
    }))
}

export const matchSummarySentence = (summary: MatchSummary): string => {
  return [
    ...(summary.writtenOff > 0 ? [`${formatSignedCents(summary.writtenOff)} unbilled work will be written off`] : []),
    ...(summary.overcharge > 0 ? [`${formatSignedCents(-summary.overcharge)} overcharge will stand`] : []),
    `balance changes by ${formatSignedCents(summary.balanceChange)}`,
  ].join('; ') + '.'
}

export const matchButtonLabel = (selectedCount: number): string => {
  if (selectedCount === 0) {
    return 'Match billed price'
  }
  return `Match billed price on ${lineCount(selectedCount)}`
}

export const matchConfirmTitle = (selectedCount: number): string => {
  return `Match the billed price on ${lineCount(selectedCount)}?`
}

export const getMatchedMessage = (selectedCount: number): string => {
  return `Matched the billed price on ${lineCount(selectedCount)}.`
}

export const toInvoicedLineChoices = (lines: InvoicedLine[], choices: InvoicedLineChoices): InvoicedLineChoice[] => {
  return lines
    .filter(line => choices[line.lineId] !== undefined)
    .map(line => ({ lineId: line.lineId, resolution: choices[line.lineId] }))
}

export const toResolveInvoicedLinesRequest = (
  request: UnbilledWorkRequest | null,
  lines: InvoicedLineChoice[]
): ResolveInvoicedLinesRequest | null => {
  if (request === null || lines.length === 0) {
    return null
  }
  return { ...request, lines }
}

export const summarizeChoices = (lines: InvoicedLine[], choices: InvoicedLineChoice[]): ChoiceSummary => {
  return choices.reduce((summary, choice) => {
    const amount = lines.find(line => line.lineId === choice.lineId)?.amount ?? 0
    if (choice.resolution === InvoicedLineResolutions.Bill) {
      return { ...summary, billCount: summary.billCount + 1, billTotal: summary.billTotal + amount }
    }
    return { ...summary, writeOffCount: summary.writeOffCount + 1, writeOffTotal: summary.writeOffTotal + amount }
  }, { writeOffCount: 0, writeOffTotal: 0, billCount: 0, billTotal: 0 })
}

const lineCount = (count: number): string => {
  return `${count} ${count === 1 ? 'line' : 'lines'}`
}

const discrepancyLabel = (job: DiscrepancyJob): string => {
  return [
    ...(job.unchargedLineCount > 0
      ? [formatCents(job.unchargedAmount), `${job.unchargedLineCount} uncharged ${job.unchargedLineCount === 1 ? 'line' : 'lines'}`]
      : []),
    ...(job.repricedLineCount > 0 ? [`${job.repricedLineCount} repriced ${job.repricedLineCount === 1 ? 'line' : 'lines'}`] : []),
  ].join(' · ')
}

export const toDiscrepancyJobOptions = (jobs: DiscrepancyJob[]): JobOption[] => {
  return jobs.map(job => ({
    value: job.id,
    label: [`#${job.id}`, job.name, job.customerName, discrepancyLabel(job)].filter(part => part !== '').join(' · '),
  }))
}

export const noDiscrepancyJobsMessage = 'No jobs on this tenant have invoiced lines that were never charged or were repriced. Turn on Show all jobs to pick any job.'

export const unchargedSummary = (lines: InvoicedLine[]): string => {
  if (lines.length === 0) {
    return 'No invoiced lines were charged $0.'
  }
  return `${lineCount(lines.length)} marked invoiced but charged $0.`
}

export const repricedSummary = (lines: InvoicedLine[]): string => {
  if (lines.length === 0) {
    return 'No invoiced lines changed price after invoicing.'
  }
  return `${lineCount(lines.length)} changed price after invoicing.`
}

export const resolveButtonLabel = (choiceCount: number): string => {
  if (choiceCount === 0) {
    return 'Submit choices'
  }
  return `Submit ${choiceCount} ${choiceCount === 1 ? 'choice' : 'choices'}`
}

export const resolveConfirmTitle = (choiceCount: number): string => {
  return `Resolve ${lineCount(choiceCount)}?`
}

export const choiceSummaryLines = (summary: ChoiceSummary): string[] => {
  return [
    ...(summary.writeOffCount > 0 ? [`Write off ${lineCount(summary.writeOffCount)} (${formatCents(summary.writeOffTotal)})`] : []),
    ...(summary.billCount > 0 ? [`Bill ${lineCount(summary.billCount)} (${formatCents(summary.billTotal)})`] : []),
  ]
}

export const getResolvedMessage = (choices: InvoicedLineChoice[]): string => {
  const writtenOff = choices.filter(choice => choice.resolution === InvoicedLineResolutions.WriteOff).length
  const billed = choices.length - writtenOff
  if (billed === 0) {
    return `Wrote off ${lineCount(writtenOff)}.`
  }
  if (writtenOff === 0) {
    return `Released ${lineCount(billed)} for billing.`
  }
  return `Wrote off ${lineCount(writtenOff)} and released ${lineCount(billed)} for billing.`
}

const workareaCount = (count: number): string => {
  return `${count} ${count === 1 ? 'work area' : 'work areas'}`
}

export const getResultSummary = (result: UnbilledWorkResult): string => {
  const count = result.workareas.length
  if (count === 0) {
    return 'No unbilled Time & Materials or Flat Rate work on this job.'
  }
  return `${workareaCount(count)} with unbilled work.`
}

export const markButtonLabel = (selectedCount: number): string => {
  if (selectedCount === 0) {
    return 'Mark unbillable'
  }
  return `Mark ${workareaCount(selectedCount)} unbillable`
}

export const confirmTitle = (selectedCount: number): string => {
  return `Mark ${workareaCount(selectedCount)} unbillable?`
}

export const getMarkedMessage = (selectedCount: number): string => {
  return `Marked the unbilled work in ${workareaCount(selectedCount)} unbillable.`
}

export const getLoadErrorMessage = (error: unknown): string => {
  return getAdminErrorMessage(error, 'Failed to load the job\'s unbilled work. Please try again.')
}

export const getMarkErrorMessage = (error: unknown): string => {
  return getAdminErrorMessage(error, 'Failed to mark the work unbillable. Please try again.')
}

export const getResolveErrorMessage = (error: unknown): string => {
  return getAdminErrorMessage(error, 'Failed to resolve the invoiced lines. Please try again.')
}

export const getMatchErrorMessage = (error: unknown): string => {
  return getAdminErrorMessage(error, 'Failed to match the billed price. Please try again.')
}
